import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "./Logo";
import { motion } from "framer-motion";
import { Menu, X, Bell, Settings, User, LogOut } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NavBar: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthenticated, user, handleLogout } = useAuth();

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <motion.nav
      className={`w-full py-3 md:py-4 border-b ${
        isScrolled
          ? "border-surface-border bg-white/90"
          : "border-transparent bg-white/80"
      } backdrop-blur-md fixed top-0 z-50 transition-all duration-200`}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <Logo />
        </Link>

        <div className="hidden md:flex items-center space-x-6">
          <Link
            to="/browse"
            className={`text-text-secondary hover:text-primary-500 transition ${
              location.pathname === "/browse"
                ? "text-primary-500 font-medium"
                : ""
            }`}
          >
            Browse Ideas
          </Link>
          <Link
            to="/how-it-works"
            className={`text-text-secondary hover:text-primary-500 transition ${
              location.pathname === "/how-it-works"
                ? "text-primary-500 font-medium"
                : ""
            }`}
          >
            How It Works
          </Link>
        </div>

        <div className="hidden md:flex items-center space-x-4">
          {isAuthenticated ? (
            <>
              <span className="text-sm text-text-secondary">
                {user?.role === "ADMIN" ? "👑 " : ""}
                {user?.name}
              </span>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <User size={20} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>
                    {user?.name}
                    <div className="text-xs font-normal text-muted-foreground mt-1">
                      {user?.email}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer w-full">
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer w-full">
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === "ADMIN" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer w-full">
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-destructive"
                  >
                    <LogOut size={16} className="mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                asChild
              >
                <Link to="/notifications">
                  <Bell size={20} />
                </Link>
              </Button>

              <Button asChild>
                <Link to="/create">Create Campaign</Link>
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" asChild>
                <Link to="/login">Log In</Link>
              </Button>
              <Button asChild>
                <Link to="/register">Sign Up</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-surface-border shadow-lg pb-4 px-4">
          <div className="flex flex-col space-y-4 pt-2">
            <Link
              to="/browse"
              className={`p-2 rounded-md ${
                location.pathname === "/browse"
                  ? "bg-primary-50 text-primary-500 font-medium"
                  : "text-text-secondary"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Browse Ideas
            </Link>
            <Link
              to="/how-it-works"
              className={`p-2 rounded-md ${
                location.pathname === "/how-it-works"
                  ? "bg-primary-50 text-primary-500 font-medium"
                  : "text-text-secondary"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              How It Works
            </Link>

            {isAuthenticated ? (
              <>
                <Link
                  to="/profile"
                  className={`p-2 rounded-md ${
                    location.pathname === "/profile"
                      ? "bg-primary-50 text-primary-500 font-medium"
                      : "text-text-secondary"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Profile
                </Link>
                <Link
                  to="/settings"
                  className={`p-2 rounded-md ${
                    location.pathname === "/settings"
                      ? "bg-primary-50 text-primary-500 font-medium"
                      : "text-text-secondary"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Settings
                </Link>
                <Link
                  to="/notifications"
                  className={`p-2 rounded-md ${
                    location.pathname === "/notifications"
                      ? "bg-primary-50 text-primary-500 font-medium"
                      : "text-text-secondary"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Notifications
                </Link>
                {user?.role === "ADMIN" && (
                  <Link
                    to="/admin"
                    className={`p-2 rounded-md ${
                      location.pathname === "/admin"
                        ? "bg-primary-50 text-primary-500 font-medium"
                        : "text-text-secondary"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Admin Dashboard
                  </Link>
                )}
                <div className="border-t border-surface-border pt-4 mt-2 flex flex-col space-y-3">
                  <Button className="w-full justify-center" asChild>
                    <Link to="/create" onClick={() => setIsMenuOpen(false)}>
                      Create Campaign
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-center text-destructive border-destructive/30"
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogOut size={16} className="mr-2" />
                    Log Out
                  </Button>
                </div>
              </>
            ) : (
              <div className="border-t border-surface-border pt-4 mt-2 flex flex-col space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-center"
                  asChild
                >
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    Log In
                  </Link>
                </Button>
                <Button className="w-full justify-center" asChild>
                  <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                    Sign Up
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.nav>
  );
};

export default NavBar;
